import { Icon } from '@iconify/react';
import {
  Avatar,
  Button,
  cn,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  ScrollShadow,
  Spacer,
  Tooltip,
  useDisclosure,
} from '@nextui-org/react';
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';

import Sidebar from 'Components/sidebar/sidebar';
import SidebarDrawer from 'Components/sidebar/sidebar-drawer';
import { useSidebarItems } from 'Components/sidebar/sidebar-items';
import logo from '../../assets/logo.png';
import { useAuthQuery } from '../../hooks/useAuthQuery';
import { useUserStore } from '../../store/use-user-store';

interface User {
  email: string;
  first_name: string;
  last_name: string;
}

const Logo = () => {
  return (
    <div className="flex h-8 w-8 items-center justify-center mb-1">
      <img src={logo} alt="LiFT Logo" />
    </div>
  );
};

const TenantName = ({ isCollapsed }: { isCollapsed: boolean }) => {
  return (
    <span
      className={cn('w-full text-small font-bold opacity-100', {
        'w-0 opacity-0': isCollapsed,
      })}
    >
      <span className="text-[20px]">Mentora LiFT</span>
    </span>
  );
};

const ApplicationLayout = () => {
  const location = useLocation();
  const { logout } = useAuthQuery();
  const user = useUserStore((state) => state.user) as User | null;
  const { hasAdminPermissions } = useUserStore();
  const sidebarItems = useSidebarItems(hasAdminPermissions);
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(true);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const onToggle = React.useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const { isOpen, onOpenChange, onClose } = useDisclosure();
  const isSidebarCompact = !isMobile && isCollapsed;
  const selectedSidebarKeys = React.useMemo(
    () => new Set([location.pathname]),
    [location.pathname]
  );

  const handleSidebarSelect = React.useCallback(() => {
    if (isMobile) {
      onClose();
    }
  }, [isMobile, onClose]);

  const handleLogout = () => {
    logout.mutate();
  };

  return (
    <div className="flex h-dvh min-h-0 w-full gap-x-3 overflow-hidden p-2">
      <SidebarDrawer
        className={cn('min-w-[288px] rounded-lg', {
          'min-w-[76px]': isSidebarCompact,
        })}
        hideCloseButton={true}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
      >
        <div
          className={cn(
            'will-change relative flex h-full w-72 flex-col bg-default-100 p-6 transition-width',
            {
              'w-[83px] items-center px-[6px] py-6': isSidebarCompact,
            }
          )}
        >
          <div
            className={cn('flex items-center gap-3 pl-2', {
              'justify-center gap-0 pl-0': isSidebarCompact,
            })}
          >
            <Logo />
            <TenantName isCollapsed={isSidebarCompact} />
            <div className={cn('flex-end flex', { hidden: isSidebarCompact })}>
              <Icon
                className="cursor-pointer dark:text-primary-foreground/60 [&>g]:stroke-[1px]"
                icon="solar:round-alt-arrow-left-line-duotone"
                width={24}
                onClick={isMobile ? onOpenChange : onToggle}
              />
            </div>
          </div>

          <ScrollShadow className="-mr-6 h-full max-h-full py-6 pr-6">
            <Sidebar
              defaultSelectedKey="home"
              isCompact={isSidebarCompact}
              items={sidebarItems}
              selectedKeys={selectedSidebarKeys}
              onSelect={handleSidebarSelect}
            />
          </ScrollShadow>
          <Spacer y={8} />

          <div
            className={cn('mt-auto flex flex-col', {
              'items-center': isSidebarCompact,
            })}
          >
            {isSidebarCompact && (
              <Tooltip content="Expand">
                <Button
                  isIconOnly
                  className="flex h-10 w-10 text-default-600"
                  size="sm"
                  variant="light"
                  onClick={onToggle}
                >
                  <Icon
                    className="cursor-pointer dark:text-primary-foreground/60 [&>g]:stroke-[1px]"
                    height={24}
                    icon="solar:round-alt-arrow-right-line-duotone"
                    width={24}
                  />
                </Button>
              </Tooltip>
            )}
            <div className="flex items-center gap-2">
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    className={cn(
                      'justify-start text-default-500 data-[hover=true]:text-foreground',
                      {
                        'justify-center': isSidebarCompact,
                      }
                    )}
                    isIconOnly={isSidebarCompact}
                    endContent={
                      isSidebarCompact ? null : (
                        <Icon
                          className="flex-none text-default-500"
                          icon="gg:select"
                          width={24}
                        />
                      )
                    }
                    variant="light"
                  >
                    {isSidebarCompact ? (
                      <Icon
                        className="rotate-180 text-default-500"
                        icon="solar:minus-circle-line-duotone"
                        width={24}
                      />
                    ) : (
                      <React.Fragment>
                        <div className="flex items-center gap-3 px-0 sm:px-3">
                          <Avatar isBordered size="sm" />
                          <div
                            className={cn('flex max-w-full flex-col', {
                              hidden: isSidebarCompact,
                            })}
                          >
                            <p className="text-small font-medium text-foreground text-start">
                              {user
                                ? `${user.first_name} ${user.last_name}`
                                : 'User'}
                            </p>
                            <p className="text-tiny font-medium text-default-400">
                              {user?.email}
                            </p>
                          </div>
                        </div>
                      </React.Fragment>
                    )}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu aria-label="User actions">
                  <DropdownItem key="user-info" className="py-2" isReadOnly>
                    <div className="flex flex-col items-start">
                      <span className="text-small font-medium truncate max-w-[150px]">
                        {user
                          ? `${user.first_name} ${user.last_name}`
                          : 'Guest User'}
                      </span>
                      <span className="text-xs text-default-500 truncate max-w-[150px]">
                        {user?.email}
                      </span>
                    </div>
                  </DropdownItem>

                  {/* <DropdownItem key="theme" className="py-2">
                    <div className="flex items-center gap-2">
                      <ThemeSwitcher />
                    </div>
                  </DropdownItem> */}
                  <DropdownItem
                    key="logout"
                    className="text-danger"
                    onPress={handleLogout}
                    startContent={<Icon icon="ic:baseline-logout" width={20} />}
                  >
                    Log Out
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        </div>
      </SidebarDrawer>

      <ScrollShadow className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
        <main className="flex h-full min-h-0 flex-1 flex-col">
          <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-0 sm:rounded-large sm:border-small sm:border-default-200">
            {isMobile && (
              <header className="sticky top-0 z-50 flex w-full shrink-0 items-center justify-between bg-background px-0 pt-3 sm:px-6 -mb-4">
                <Button
                  isIconOnly
                  className="flex text-default-500 sm:hidden"
                  size="sm"
                  variant="light"
                  onPress={onOpenChange}
                >
                  <Icon
                    height={24}
                    icon="solar:hamburger-menu-outline"
                    width={24}
                  />
                </Button>
              </header>
            )}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden mt-2 sm:mt-0">
              <Outlet />
            </div>
          </div>
        </main>
      </ScrollShadow>
    </div>
  );
};

export default ApplicationLayout;
